import { ref, isRef, unref, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { errorHandler } from '@/core/composables/handlers/errorHandler'

export interface FormRequestOptions {
  method: string
  endpoint: string
  form: any
  onSuccess: (response: any) => void
  onError?: (error: any) => void
}

export function useFormRequest() {
  const { errors, handleFormErrors, resetErrors } = errorHandler()

  function submitRequest({
                           method,
                           endpoint,
                           form,
                           onSuccess,
                           onError = () => {},
                         }: FormRequestOptions) {
    const status: Ref<'idle' | 'pending' | 'success' | 'error'> = ref('idle')
    const router = useRouter()

    async function onSubmit() {
      status.value = 'pending'
      resetErrors()

      // Unwrap the form ref if necessary
      const payload = isRef(form) ? unref(form) : form

      try {
        // Ensure endpoint starts with a slash
        const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
        const data = await $fetch(url, {
          method: method.toUpperCase(),
          body: payload,
        })

        if (data.status === 'error') {
          if (data.error?.payload) {
            handleFormErrors(data.error.payload)
            status.value = 'error'
            onError(data.error.payload)
          } else if (data.status_code === 401) {
            await router.push('/auth/login')
          } else {
            status.value = 'error'
            onError(data)
          }
        } else if (data.status === 'success') {
          status.value = 'success'
          onSuccess(data.data)
        }
      } catch (err: any) {
        // Handle thrown validation errors and other non-2xx responses
        const errData = err.data || err.response?._data || null
        if (errData?.status === 'error' && errData.error?.payload) {
          handleFormErrors(errData.error.payload)
          status.value = 'error'
          onError(errData.error.payload)
        } else if (err.response?.status === 401 || errData?.status_code === 401) {
          await router.push('/auth/login')
        } else {
          status.value = 'error'
          onError(err)
        }
      }
    }

    return { onSubmit, status, errors, resetErrors }
  }

  return { submitRequest, resetErrors }
}
